import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Star, MapPin, Calendar, Quote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatSafariDate = (iso) => {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      year: 'numeric',
    });
  } catch (_) {
    return iso;
  }
};

const StarRating = ({ rating = 5, size = 16 }) => (
  <div className="flex items-center space-x-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        className={n <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
      />
    ))}
  </div>
);

const PackageReviews = ({ packageId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!packageId) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/testimonials?package_id=${packageId}`);
        setReviews(res.data);
      } catch (err) {
        console.error('Failed to load reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [packageId]);

  if (loading) return null;
  if (reviews.length === 0) return null;

  // Calculate average rating
  const avgRating = (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1);

  return (
    <section className="py-20 px-4 bg-cream/40" data-testid="package-reviews-section">
      <div className="max-w-5xl mx-auto">
        {/* Header with aggregate rating */}
        <div className="text-center mb-12">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-emerald-700 mb-3 block">
            Guest Experiences
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-normal text-navy mb-4 leading-tight">
            Verified Reviews
          </h2>

          <div className="inline-flex items-center space-x-3 bg-white px-6 py-3 rounded-full shadow-md">
            <StarRating rating={Math.round(parseFloat(avgRating))} size={20} />
            <span className="price-luxe-bold text-2xl text-navy">{avgRating}</span>
            <span className="font-body text-sm text-muted-foreground">
              · {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
            </span>
          </div>
        </div>

        {/* Reviews grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.map((review, idx) => (
            <motion.div
              key={review.id || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="bg-white rounded-xl shadow-md p-6 relative border border-emerald-100 hover:shadow-lg transition"
              data-testid={`review-${idx}`}
            >
              <Quote size={32} className="absolute top-4 right-4 text-emerald-100" />

              <StarRating rating={review.rating || 5} />

              <p className="font-body text-foreground/85 leading-relaxed my-4 italic">
                &ldquo;{review.comment}&rdquo;
              </p>

              <div className="border-t border-emerald-50 pt-4 flex items-start justify-between">
                <div>
                  <p className="font-heading text-base text-navy">
                    {review.name}
                  </p>
                  <p className="font-body text-xs text-muted-foreground flex items-center mt-1">
                    <MapPin size={11} className="mr-1 text-emerald-600" />
                    {review.location}
                  </p>
                </div>
                {review.safari_date && (
                  <div className="text-right">
                    <p className="font-accent text-[10px] uppercase tracking-wider text-muted-foreground">
                      Safari
                    </p>
                    <p className="font-body text-xs text-emerald-700 flex items-center">
                      <Calendar size={11} className="mr-1" />
                      {formatSafariDate(review.safari_date)}
                    </p>
                  </div>
                )}
              </div>

              {/* "Verified Traveler" trust badge */}
              <div className="absolute -top-2 -left-2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow">
                ✓ Verified
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PackageReviews;
